import { useState, useEffect, Fragment } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import EventSpaceNav from "@/components/EventSpaceNav";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitcher from "@/components/LangSwitcher";
import Seo from "@/components/Seo";
import { Home, User, Newspaper, Calendar, Users, MessageSquare, Folder, Settings, CalendarCheck, HeadphonesIcon, LogOut, Kanban, Package, UsersRound, Menu, X, UserCircle, Sliders as SettingsIcon, ShieldCheck, Mail, Send, BarChart3, History, Bell, Trophy, RefreshCw, Sparkles, GraduationCap, ScanLine, BookOpen, Handshake, Wine, Megaphone, Rocket, ClipboardList, Inbox, FileText, Film } from "lucide-react";
import UniversalSearch from "@/components/UniversalSearch";
import { api } from "@/lib/api";

// Map raw user.role values to known i18n keys. Returns "" if unknown so we fall
// back to the raw role label rendered as-is.
const ROLE_I18N_KEYS = {
  admin: "role_admin",
  member: "role_member",
  press: "role_press",
  applicant: "role_applicant",
  event_visitor: "role_event_visitor",
  journalist: "role_journalist",
};
function dict_role_label(role, t) {
  const key = ROLE_I18N_KEYS[role];
  if (!key) return "";
  const v = t(key);
  return v === key ? "" : v; // t returns the key itself when missing
}

export default function PortalLayout({ children, sidebar = "member" }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const inEventSpace = pathname.startsWith("/portal/event/");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetch = () => {
      api.get("/notifications/unread-count").then((r) => { if (mounted) setUnread(r.data.unread || 0); }).catch(() => {});
      api.get("/messages/unread-count").then((r) => { if (mounted) setMsgUnread(r.data.unread || 0); }).catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [user]);

  const items = sidebar === "press"
    ? [
        { to: "/press", label: t("sidebar_press_dashboard"), icon: Newspaper, end: true },
        { to: "/press/releases", label: t("sidebar_press_releases"), icon: Newspaper },
        { to: "/press/events", label: t("sidebar_press_events"), icon: Calendar },
        { to: "/press/members", label: t("sidebar_press_members"), icon: Users },
        { to: "/press/resources", label: t("sidebar_press_resources"), icon: Folder },
        { to: "/press/help", label: t("sidebar_help_mode_emploi"), icon: BookOpen },
        { to: "/portal/me", label: t("sidebar_personal_profile"), icon: UserCircle },
      ]
    : sidebar === "admin"
      ? [
          { to: "/admin", label: t("sidebar_admin_dashboard"), icon: Home, end: true },
          { to: "/admin/analytics", label: t("sidebar_admin_analytics"), icon: BarChart3 },
          { to: "/admin/prospects", label: t("sidebar_admin_prospects"), icon: Kanban },
          { to: "/admin/applications", label: "Candidatures", icon: FileText },
          { to: "/admin/members", label: t("sidebar_admin_members"), icon: Users },
          { to: "/admin/contacts", label: t("sidebar_admin_contacts"), icon: UsersRound },
          // Sprint 81 — Candidatures fusionnées dans Prospects (status="candidature").
          // Sprint 92o — Réintroduction du menu Candidatures dédié (accès direct base candidats).
          { to: "/admin/news", label: t("sidebar_admin_news"), icon: Newspaper },
          { to: "/admin/events", label: t("sidebar_admin_events"), icon: Calendar },
          { to: "/admin/event-submissions", label: "Soumissions événements", icon: Inbox },
          { to: "/admin/formations", label: t("sidebar_admin_formations"), icon: GraduationCap },
          { to: "/admin/shared-services", label: "Services mutualisés", icon: Handshake },
          { to: "/admin/resources", label: "Ressources", icon: Folder },
          { to: "/admin/club", label: "Le Club", icon: Wine },
          { to: "/admin/petitions", label: "Pétitions", icon: Megaphone },
          { to: "/admin/opportunities", label: "Opportunités", icon: Rocket },
          { to: "/admin/forms", label: "Formulaires", icon: ClipboardList },
          { to: "/admin/scan", label: t("sidebar_admin_checkin_qr"), icon: ScanLine },
          { to: "/admin/meetings", label: t("sidebar_admin_meetings"), icon: CalendarCheck },
          { to: "/admin/users", label: t("sidebar_admin_administrators"), icon: ShieldCheck },
          { to: "/admin/emailing", label: "Emailing", icon: Mail },
          { to: "/admin/digest", label: "Weekly Digest", icon: Mail },
          { to: "/admin/scoring", label: t("sidebar_admin_scoring"), icon: Trophy },
          { to: "/admin/renewals", label: t("sidebar_admin_renewals"), icon: RefreshCw },
          { to: "/admin/activity", label: t("sidebar_admin_activity_log"), icon: History },
          { to: "/admin/ai-feedback", label: t("sidebar_admin_ai_feedback"), icon: Sparkles },
          { to: "/admin/help", label: t("sidebar_help_mode_emploi"), icon: BookOpen },
          { to: "/admin/settings", label: t("sidebar_admin_settings"), icon: SettingsIcon },
          { to: "/portal/me", label: t("sidebar_personal_profile"), icon: UserCircle },
        ]
      : [
          { to: "/portal", label: t("sidebar_member_dashboard"), icon: Home, end: true },
          { to: "/portal/me", label: t("sidebar_personal_profile"), icon: UserCircle },
          { to: "/portal/profile", label: t("sidebar_member_profile"), icon: User },
          { to: "/portal/notifications", label: t("sidebar_member_notifications"), icon: Bell, showUnread: true },
          { to: "/portal/news", label: t("sidebar_member_news"), icon: Newspaper },
          { to: "/portal/events", label: t("sidebar_member_events"), icon: Calendar },
          { to: "/portal/directory", label: t("sidebar_member_directory"), icon: Users },
          { to: "/portal/meetings", label: t("sidebar_member_meetings"), icon: CalendarCheck },
          { to: "/portal/messages", label: t("sidebar_member_messages"), icon: MessageSquare, showMsgUnread: true },
          { to: "/portal/matchmaking", label: t("sidebar_member_matchmaking"), icon: Sparkles },
          { to: "/portal/shared-services", label: t("sidebar_member_shared_services"), icon: Handshake },
          { to: "/portal/resources", label: t("sidebar_member_resources"), icon: Folder },
          { to: "/portal/media", label: t("sidebar_member_media"), icon: Film },
          { to: "/portal/club", label: "Le Club", icon: Wine },
          { to: "/portal/help", label: t("sidebar_help_mode_emploi"), icon: BookOpen },
        ];

  const userInitials = user?.first_name ? `${user.first_name[0]}${user.last_name?.[0] || ""}` : "U";

  const sidebarContent = (
    <>
      <Logo subtitle={sidebar === "admin" ? t("subtitle_admin") : sidebar === "press" ? t("subtitle_press") : t("subtitle_member")} />
      <nav className="mt-6 flex flex-col gap-1">
        {items.map((it) => {
          const isEvents = /^\/(portal|press|admin)\/events$/.test(it.to);
          return (
          <Fragment key={it.to}>
          <NavLink
            to={it.to}
            end={it.end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
                isActive || (isEvents && inEventSpace) ? "bg-wine-50 text-wine-700" : "text-neutral-600 hover:bg-wine-50/50 hover:text-wine-700"
              }`
            }
            data-testid={`sidebar-${it.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <it.icon className="h-4 w-4" />
            <span className="flex-1">{it.label}</span>
            {it.showUnread && unread > 0 && <span className="rounded-full bg-wine-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="sidebar-unread-badge">{unread}</span>}
            {it.showMsgUnread && msgUnread > 0 && <span className="rounded-full bg-wine-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="sidebar-msg-badge">{msgUnread}</span>}
          </NavLink>
          {isEvents && user && <EventSpaceNav onNavigate={() => setMobileOpen(false)} />}
          </Fragment>
          );
        })}
      </nav>

      <div className="mt-auto rounded-2xl border border-wine-100 bg-wine-50/60 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-wine-700">
          <HeadphonesIcon className="h-4 w-4" />
          {t("sidebar_need_help")}
        </div>
        <p className="mt-1.5 text-xs text-neutral-600">{t("sidebar_help_text")}</p>
        <button onClick={() => nav(`/contact?source=${sidebar === "press" ? "press-help" : "portal-help"}`)} className="mt-3 w-full rounded-full border border-wine-300 bg-white px-3 py-1.5 text-xs font-semibold text-wine-700 transition-colors hover:bg-wine-100" data-testid="sidebar-contact">
          {t("sidebar_contact_us")}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-3 border-t border-neutral-100 pt-4">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-wine-100 text-sm font-semibold text-wine-700">{userInitials}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-neutral-900" data-testid="sidebar-user-name">{user?.name || t("user_label_default")}</div>
          <div className="truncate text-xs text-neutral-500">{user?.role ? (dict_role_label(user.role, t) || user.role) : ""}</div>
        </div>
        <button onClick={() => logout().then(() => nav("/"))} className="text-neutral-400 hover:text-wine-600" data-testid="sidebar-logout" aria-label={t("logout")}>
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-wine-50/20">
      <Seo
        title={sidebar === "admin" ? t("title_admin_dashboard") : sidebar === "press" ? t("title_press_portal") : t("title_member_portal")}
        path={sidebar === "admin" ? "/admin" : sidebar === "press" ? "/press" : "/portal"}
        noindex
      />
      {/* Desktop Sidebar */}
      <aside className="hidden w-64 flex-col border-r border-neutral-100 bg-white px-5 py-6 lg:flex">
        {sidebarContent}
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
            aria-label={t("layout_portallayout_90")}
            data-testid="mobile-drawer-backdrop"
          />
          <aside className="relative flex h-full w-72 max-w-[85vw] flex-col border-r border-neutral-100 bg-white px-5 py-6 shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"
              aria-label={t("layout_portallayout_90")}
              data-testid="mobile-drawer-close"
            >
              <X className="h-5 w-5" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b border-neutral-100 bg-white px-4 py-3 lg:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-full hover:bg-wine-50"
            aria-label={t("layout_portallayout_91")}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5 text-neutral-700" />
          </button>
          <Logo />
          <div className="flex items-center gap-2">
            <UniversalSearch />
            <LangSwitcher />
          </div>
        </div>
        {/* Desktop top bar */}
        <div className="hidden items-center justify-end gap-3 border-b border-neutral-100 bg-white px-8 py-3 lg:flex">
          <UniversalSearch />
          <LangSwitcher />
          <Link to="/" className="text-xs font-medium text-neutral-500 hover:text-wine-600" data-testid="back-to-site">← {t("nav_back_to_site")}</Link>
        </div>
        <div className="min-w-0 px-4 py-6 sm:px-6 md:px-10 md:py-8">{children}</div>
      </div>
    </div>
  );
}
